import { t } from '../i18n';
import { ENVIRONMENT_ID, type EnvironmentId } from '../config/constants';
import { getSetting, setSetting, subscribeSettings } from '../settings/userSettings';

export type LandingEnterHandler = (btn: HTMLButtonElement, status: HTMLElement) => void;

/**
 * Wire up the 2D landing page: title/subtitle/CTA, WebXR support probe, and
 * invoke `onEnter` once the user clicks "Enter VR".
 */
export function bootstrapLanding(onEnter: LandingEnterHandler): void {
  const titleEl = document.querySelector('h1');
  const subtitleEl = document.querySelector('main p');
  const btn = document.getElementById('enter-vr') as HTMLButtonElement | null;
  const status = document.getElementById('status');
  if (!btn || !status || !titleEl || !subtitleEl) return;

  titleEl.textContent = t('appTitle');
  subtitleEl.textContent = t('appSubtitle');
  btn.textContent = t('loading');

  const versionEl = document.getElementById('app-version');
  if (versionEl) versionEl.textContent = t('appVersion')(__APP_VERSION__);

  bootstrapEnvironmentSelector();
  bootstrapAudioControls();

  if (!('xr' in navigator) || !navigator.xr) {
    failLanding(btn, status, t('webxrNotSupported'));
    return;
  }

  navigator.xr
    .isSessionSupported('immersive-vr')
    .then((supported) => {
      if (!supported) {
        failLanding(btn, status, t('webxrNotSupported'));
        return;
      }
      btn.textContent = t('enterVR');
      btn.disabled = false;
      // No { once: true }: the listener must survive across sessions. Re-entrancy is
      // guarded by btn.disabled (set at the top of startXR); a disabled button emits no click.
      btn.addEventListener('click', () => onEnter(btn, status));
    })
    .catch(() => failLanding(btn, status, t('webxrNotSupported')));
}

/**
 * Wire the landing environment selector. The choice persists in `UserSettings`
 * (synced to the cloud) and is applied at the next "Enter VR".
 */
function bootstrapEnvironmentSelector(): void {
  const label = document.getElementById('env-label');
  const options = document.getElementById('env-options');
  if (!label || !options) return;

  label.textContent = `${t('envLabel')}:`;

  const names: Record<EnvironmentId, string> = {
    [ENVIRONMENT_ID.forest]: t('envForest'),
    [ENVIRONMENT_ID.salaDArmi]: t('envSalaDArmi'),
    [ENVIRONMENT_ID.passthrough]: t('envPassthrough'),
  };
  const buttons = new Map<EnvironmentId, HTMLButtonElement>();
  const paintActive = (): void => {
    const current = getSetting('environment');
    for (const [id, b] of buttons) b.classList.toggle('active', id === current);
  };
  const addOption = (id: EnvironmentId): void => {
    const b = document.createElement('button');
    b.type = 'button';
    b.textContent = names[id];
    b.addEventListener('click', () => setSetting('environment', id));
    options.appendChild(b);
    buttons.set(id, b);
  };

  // Virtual environments are always available.
  addOption(ENVIRONMENT_ID.forest);
  addOption(ENVIRONMENT_ID.salaDArmi);
  paintActive();

  // Passthrough needs immersive-ar (camera) support — only offer it where the headset can
  // show the real surroundings (e.g. Quest). Probed async; the button is appended last,
  // once support is confirmed.
  navigator.xr
    ?.isSessionSupported('immersive-ar')
    .then((supported) => {
      if (!supported) return;
      addOption(ENVIRONMENT_ID.passthrough);
      paintActive();
    })
    .catch(() => {});

  // Repaint on any settings change — covers both the click above and a cloud
  // pull (login can land after the page is painted).
  subscribeSettings(paintActive);
}

/**
 * Wire the landing volume sliders (SFX + music). Both persist to `UserSettings`
 * (synced to the cloud) and are read live by the shared AudioManager bus. Lives on the
 * landing — not just the user menu — so it stays reachable in local-only mode, where the
 * music actually plays.
 */
function bootstrapAudioControls(): void {
  const sfxLabel = document.getElementById('sfx-label');
  const musicLabel = document.getElementById('music-label');
  const sfx = document.getElementById('sfx-vol') as HTMLInputElement | null;
  const music = document.getElementById('music-vol') as HTMLInputElement | null;
  if (!sfxLabel || !musicLabel || !sfx || !music) return;

  sfxLabel.textContent = t('audioSfxLabel');
  musicLabel.textContent = t('audioMusicLabel');

  const paint = (): void => {
    sfx.value = String(Math.round(getSetting('sfxVolume') * 100));
    music.value = String(Math.round(getSetting('musicVolume') * 100));
  };
  paint();
  sfx.addEventListener('input', () => setSetting('sfxVolume', pctToUnit(sfx.value)));
  music.addEventListener('input', () => setSetting('musicVolume', pctToUnit(music.value)));
  // Repaint on a cloud pull or a settings reset (login can land after the page is painted).
  subscribeSettings(paint);
}

/** Map a 0–100 slider string to a clamped 0–1 unit value. */
function pctToUnit(raw: string): number {
  const n = Number(raw) / 100;
  return Math.min(1, Math.max(0, Number.isFinite(n) ? n : 0));
}

export function failLanding(btn: HTMLButtonElement, status: HTMLElement, msg: string): void {
  status.textContent = msg;
  status.classList.add('error');
  btn.textContent = t('enterVR');
  btn.disabled = false;
}
